import React, { useState } from "react";
import { Link } from "react-router-dom";
import { productSuites } from "./Products";
import "./Ecosystem3DVisual.css";

// SVG calculation constants for 540x540 viewBox
const SVG_CENTER = 270;
const SVG_RADIUS = 210;

export default function Ecosystem3DVisual() {
  const [hoveredIdx, setHoveredIdx] = useState(null);

  // Use the 12 canonical products directly from Products.js
  const products = productSuites.slice(0, 12);

  // Compute radial SVG endpoints
  const connectionLines = products.map((_, idx) => {
    const angleDeg = idx * (360 / products.length);
    const angleRad = (angleDeg - 90) * (Math.PI / 180);
    const x = SVG_CENTER + SVG_RADIUS * Math.cos(angleRad);
    const y = SVG_CENTER + SVG_RADIUS * Math.sin(angleRad);
    return { idx, x, y, angleDeg };
  });

  return (
    <div className="ecosystem-container-root">
      {/* ================= DESKTOP & TABLET: 3D ORBIT VIEW ================= */}
      <div
        className="ecosystem-stage desktop-orbit-view"
        role="region"
        aria-label="ITSoftLab Connected Ecosystem Orbit Visualization"
      >
        {/* Ambient Backdrop Glow */}
        <div className="ecosystem-backdrop-glow" aria-hidden="true" />

        {/* Stationary Concentric Orbital Rings */}
        <div className="ecosystem-track-ring ecosystem-track-inner" aria-hidden="true" />
        <div className="ecosystem-track-ring ecosystem-track-middle" aria-hidden="true" />
        <div className="ecosystem-track-ring ecosystem-track-orbit" aria-hidden="true" />

        {/* Rotating Orbit Stage (pulses & product nodes) */}
        <div className="ecosystem-orbit-track">
          {/* SVG Network Lines (rotates with nodes for 100% sync) */}
          <svg
            className="ecosystem-svg-network"
            viewBox="0 0 540 540"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id="ecoPulseGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#2563EB" stopOpacity="0.2" />
                <stop offset="50%" stopColor="#60A5FA" stopOpacity="1" />
                <stop offset="100%" stopColor="#93C5FD" stopOpacity="0.2" />
              </linearGradient>
            </defs>
            {connectionLines.map((line) => {
              const isActive = hoveredIdx === line.idx;
              return (
                <g key={`line-group-${line.idx}`}>
                  {/* Base connection line */}
                  <line
                    x1={SVG_CENTER}
                    y1={SVG_CENTER}
                    x2={line.x}
                    y2={line.y}
                    className={`eco-line-base ${isActive ? "is-active" : ""}`}
                  />
                  {/* Flow pulse animation */}
                  <line
                    x1={SVG_CENTER}
                    y1={SVG_CENTER}
                    x2={line.x}
                    y2={line.y}
                    className="eco-line-pulse"
                  />
                </g>
              );
            })}
          </svg>

          {/* 12 Orbiting Product Nodes */}
          {products.map((suite, idx) => {
            const angle = idx * 30; // 360 / 12 = 30 degrees apart
            const isExternal = Boolean(suite.link && suite.link.startsWith("http"));
            const accentClass = `accent-${suite.color || "blue"}`;
            const isHovered = hoveredIdx === idx;

            // Render icon with standard size
            const nodeIcon = React.isValidElement(suite.icon)
              ? React.cloneElement(suite.icon, { size: 20 })
              : suite.icon;

            const content = (
              <>
                {/* Node Icon Pill */}
                <div className="product-node-pill">
                  <span className="product-icon-inner">{nodeIcon}</span>
                </div>

                {/* Product Label */}
                <span className="product-node-label">{suite.title}</span>

                {/* Tooltip */}
                <div className="product-node-tooltip" role="tooltip">
                  <div className="tooltip-header">
                    <span className="tooltip-title">{suite.title}</span>
                    <span className="tooltip-badge">{suite.color || "Suite"}</span>
                  </div>
                  <p className="tooltip-desc">{suite.description}</p>
                  <div className="tooltip-arrow" />
                </div>
              </>
            );

            return (
              <div
                key={`orbit-node-${idx}-${suite.title}`}
                className="product-node-slot"
                style={{
                  transform: `translate(-50%, -50%) rotate(${angle}deg) translateY(calc(-1 * var(--eco-orbit-radius)))`,
                }}
              >
                {/* Static un-tilt to cancel slot angle */}
                <div
                  className="product-node-static-unrotate"
                  style={{ transform: `rotate(${-angle}deg)` }}
                >
                  {/* Live counter-rotation to keep node perpetually upright */}
                  <div className="product-node-live-counter">
                    {isExternal ? (
                      <a
                        href={suite.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`product-node-link ${accentClass} ${isHovered ? "is-active" : ""}`}
                        aria-label={`${suite.title} - ${suite.description}`}
                        onMouseEnter={() => setHoveredIdx(idx)}
                        onMouseLeave={() => setHoveredIdx(null)}
                        onFocus={() => setHoveredIdx(idx)}
                        onBlur={() => setHoveredIdx(null)}
                      >
                        {content}
                      </a>
                    ) : (
                      <Link
                        to={suite.link || "/contactus"}
                        className={`product-node-link ${accentClass} ${isHovered ? "is-active" : ""}`}
                        aria-label={`${suite.title} - ${suite.description}`}
                        onMouseEnter={() => setHoveredIdx(idx)}
                        onMouseLeave={() => setHoveredIdx(null)}
                        onFocus={() => setHoveredIdx(idx)}
                        onBlur={() => setHoveredIdx(null)}
                      >
                        {content}
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Stationary Central Hub (Remains perfectly still) */}
        <div className="ecosystem-central-hub" role="banner">
          <div className="hub-logo-wrap">
            <img
              src="/images/log.png"
              alt="ITSoftLab Logo"
              className="hub-logo-img"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          </div>
          <div className="hub-brand-name">ITSoftLab</div>
          <span className="hub-badge">ENTERPRISE HUB</span>
        </div>

        {/* Floating Dashboard Panel A: Unified Ecosystem */}
        <div className="floating-panel floating-panel-a" aria-hidden="true">
          <div className="panel-header-row">
            <span className="panel-icon-dot" />
            <span className="panel-title">Unified Ecosystem</span>
          </div>
          <div className="panel-main-stat">12 Integrated Solutions</div>
          <div className="panel-tags-row">
            <span className="panel-tag-pill">Enterprise Data</span>
            <span className="panel-tag-pill">Cloud-Ready</span>
            <span className="panel-tag-pill">AI Automation</span>
          </div>
          <svg className="panel-sparkline-svg" viewBox="0 0 160 20" fill="none">
            <path
              d="M 2,16 Q 30,17 55,9 T 105,8 T 135,4 T 158,2"
              stroke="#2563EB"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <circle cx="158" cy="2" r="2.5" fill="#2563EB" />
          </svg>
        </div>

        {/* Floating Dashboard Panel B: Connected Workflow */}
        <div className="floating-panel floating-panel-b" aria-hidden="true">
          <div className="panel-header-row">
            <span className="pulse-dot" />
            <span className="panel-title">Connected Workflow</span>
          </div>
          <div className="workflow-flow-row">
            <span className="workflow-node-chip">ERP</span>
            <span className="workflow-arrow">→</span>
            <span className="workflow-node-chip">Supply Chain</span>
            <span className="workflow-arrow">→</span>
            <span className="workflow-node-chip">CRM</span>
          </div>
          <div className="workflow-status-bar">
            <span className="pulse-dot" />
            <span>Synchronized Business Operations</span>
          </div>
        </div>
      </div>

      {/* ================= MOBILE & FOLDABLE ALTERNATIVE VIEW ================= */}
      <div
        className="ecosystem-mobile-view"
        role="region"
        aria-label="ITSoftLab 12 Enterprise Solutions Ecosystem"
      >
        <div className="eco-mobile-header">
          <div className="eco-mobile-hub-card">
            <div className="eco-mobile-logo-wrap">
              <img
                src="/images/log.png"
                alt="ITSoftLab Logo"
                className="hub-logo-img"
              />
            </div>
            <div className="eco-mobile-hub-info">
              <span className="eco-mobile-hub-title">Enterprise Ecosystem</span>
              <span className="eco-mobile-hub-badge">12 Connected Solutions</span>
            </div>
          </div>
        </div>

        {/* 12 Mobile Product Cards Grid */}
        <div className="eco-mobile-grid">
          {products.map((suite, idx) => {
            const isExternal = Boolean(suite.link && suite.link.startsWith("http"));
            const accentClass = `accent-${suite.color || "blue"}`;
            const nodeIcon = React.isValidElement(suite.icon)
              ? React.cloneElement(suite.icon, { size: 18 })
              : suite.icon;

            const cardInner = (
              <div className={`eco-mobile-card ${accentClass}`}>
                <div className="eco-mobile-card-top">
                  <div className="eco-mobile-icon-box">
                    {nodeIcon}
                  </div>
                  <span className="eco-mobile-tag">{suite.color || "Suite"}</span>
                </div>
                <h4 className="eco-mobile-card-title">{suite.title}</h4>
                <p className="eco-mobile-card-desc">{suite.description}</p>
                <span className="eco-mobile-link-arrow">Explore →</span>
              </div>
            );

            return isExternal ? (
              <a
                key={`mobile-prod-${idx}-${suite.title}`}
                href={suite.link}
                target="_blank"
                rel="noopener noreferrer"
                className="eco-mobile-card-anchor"
                aria-label={`${suite.title} - ${suite.description}`}
              >
                {cardInner}
              </a>
            ) : (
              <Link
                key={`mobile-prod-${idx}-${suite.title}`}
                to={suite.link || "/contactus"}
                className="eco-mobile-card-anchor"
                aria-label={`${suite.title} - ${suite.description}`}
              >
                {cardInner}
              </Link>
            );
          })}
        </div>

        {/* Synchronized Operations Footer Pill */}
        <div className="eco-mobile-status-pill">
          <span className="pulse-dot" />
          <span>Synchronized Business Operations • 12 Active Modules</span>
        </div>
      </div>
    </div>
  );
}
